/* eslint-disable max-lines-per-function */
import React, { useState } from 'react';
import FormDialog from '../../../Components/Modal/modal';
import {
  CANCEL_TEXT,
  LEAVE_UPLOAD,
  ADD_EDIT_RESPONSE_MESSAGES,
  AUTHENTICATION_FAILED,
} from '../../../Constants/strings';
import CommonDatePicker from '../../../Components/Datepicker/datepicker';
import './manualLeaveUploadPopUp.scss';
import PropTypes from 'prop-types';
import UseAxios from '../../../Api/http/http';
import { triggerLeaveUpload, triggerSyncLeaveData } from '../../../Api/request/integration';
import { useSnackbar } from '../../../Components/Snackbar/snackBarProvider';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import 'dayjs/locale/en';
dayjs.extend(customParseFormat);

dayjs.locale({
  ...dayjs.Ls.en,
  formats: {
    ...dayjs.Ls.en.formats,
    LongDate: 'DD-MM-YYYY',
  },
});

const ManualLeaveUploadPopup = ({
  openAddModal,
  handleClose,
  runBy,
  setRows,
  isSync,
}) => {
  const [startDateValue, setStartDateValue] = useState('');
  const [endDateValue, setEndDateValue] = useState('');
  const [autoFocus, setAutoFocus] = useState(false);
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const { snackbarOpen } = useSnackbar();

  const handleCloseWithReset = () => {
    resetValues();
    handleClose();
  };

  const resetValues = () => {
    setStartDateValue('');
    setEndDateValue('');
    setStartDateError('');
    setEndDateError('');
  };

  const defaultApiErrorMessage = isSync
    ? LEAVE_UPLOAD.LEAVE_SYNC_UNSUCCESSFUL : LEAVE_UPLOAD.UNABLE_TO_ADD_LEAVE_UPLOAD;

  const handleResponse = (responseData) => {
    switch (
      responseData?.data?.status || responseData?.data?.response?.data?.statusCode || responseData?.status
    ) {
    case 200:
      setRows();
      snackbarOpen(isSync
        ? LEAVE_UPLOAD.LEAVE_SYNC_STARTED_SUCCESSFULLY : LEAVE_UPLOAD.LEAVE_UPLOAD_ADD_SUCCESS,
      'success',
      );
      handleCloseWithReset();
      break;
    case 400:
      snackbarOpen(
        responseData.data.response.data.externalMessage,
        'warning',
      );
      break;
    case 401:
      snackbarOpen(AUTHENTICATION_FAILED, 'warning');
      break;
    case 500:
      snackbarOpen(ADD_EDIT_RESPONSE_MESSAGES.OOPS_TRY_AGAIN, 'warning');
      handleCloseWithReset();
      break;
    default:
      snackbarOpen(defaultApiErrorMessage, 'warning');
      handleCloseWithReset();
      break;
    }
  };


  const validateFields = (reqData) => {
    let isValid = true;

    if (!reqData.startDate) {
      setStartDateError(ADD_EDIT_RESPONSE_MESSAGES.ENTER_START_DATE);
      isValid = false;
    } else {
      setStartDateError('');
    }

    if (!reqData.endDate) {
      setEndDateError(ADD_EDIT_RESPONSE_MESSAGES.ENTER_END_DATE);
      isValid = false;
    } else {
      setEndDateError('');
    }

    return isValid;
  };

  const submitAction = async () => {
    try {

      if (startDateError || endDateError) return;

      const requestData = {
        startDate: startDateValue,
        endDate: endDateValue,
        schedulerType: 'leave-details',
        runBy,
      };

      const selectedStartDate = dayjs(startDateValue).format('DD-MMM-YYYY');
      const selectedEndDate = dayjs(endDateValue).format('DD-MMM-YYYY');

      const syncPayload = {
        startDate: selectedStartDate,
        endDate: selectedEndDate,
        lastUpdatedBy: runBy,
        schedulerType: 'sync-leave-data',
        runBy,
      };

      if (!validateFields(requestData)) return;

      const response = await UseAxios(isSync ? triggerSyncLeaveData(syncPayload) : triggerLeaveUpload(requestData));

      handleResponse(response);
      setAutoFocus(false);
    } catch (error) {
      snackbarOpen(defaultApiErrorMessage, 'warning');
      handleCloseWithReset();
      console.error('Error:', error);
    }
  };

  const handleSubmit = () => submitAction();
  const handleStartDateChange = ({ value }) => {
    const formattedDate = dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';
    const currentDate = dayjs(); // Ensure currentDate is a dayjs object
    // eslint-disable-next-line newline-per-chained-call
    const maxAllowedDate = currentDate.add(1, 'month').endOf('month').format('YYYY-MM-DD'); // End of next month
    const minDate = currentDate.subtract(2, 'month').format('YYYY-MM-DD');

    setStartDateValue(formattedDate);

    // Validate start date
    let startDateErrorMessage = '';
    const endDateErrorMessage = '';

    if (!formattedDate) {
      startDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.ENTER_START_DATE;
    } else if (new Date(formattedDate) < new Date(minDate) || new Date(formattedDate) > new Date(maxAllowedDate)) {
      startDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.SELECT_VALID_DATE_RANGE;
    } else if (endDateValue && new Date(formattedDate) > new Date(endDateValue)) {
      startDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.START_DATE_BEFORE_END_DATE;
    } else {
      setStartDateError('');
      setEndDateError('');
    }
    setStartDateError(startDateErrorMessage);
    setEndDateError(endDateErrorMessage);
  };

  const handleEndDateChange = ({ value }) => {
    const formattedDate = dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';
    const currentDate = dayjs(); // Ensure currentDate is a dayjs object
    const maxAllowedDate = currentDate.add(1, 'month').endOf('month')
      .format('YYYY-MM-DD'); // End of next month
    const minDate = currentDate.subtract(2, 'month').format('YYYY-MM-DD');

    setEndDateValue(formattedDate);

    // Validate end date
    let endDateErrorMessage = '';
    const startDateErrorMessage = '';

    if (!formattedDate) {
      endDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.ENTER_END_DATE;
    } else if (new Date(formattedDate) < new Date(minDate) || new Date(formattedDate) > new Date(maxAllowedDate)) {
      endDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.SELECT_VALID_DATE_RANGE;
    } else if (startDateValue && new Date(formattedDate) < new Date(startDateValue)) {
      endDateErrorMessage = ADD_EDIT_RESPONSE_MESSAGES.START_DATE_BEFORE_END_DATE;
    } else {
      setStartDateError('');
      setEndDateError('');
    }
    setEndDateError(endDateErrorMessage);
    setStartDateError(startDateErrorMessage);
  };

  const getMinDate = () => {
    if (isSync) {
      const currentDate = dayjs(); // Current date
      const startOfPreviousMonth = currentDate.subtract(1, 'month').startOf('month');
      return startOfPreviousMonth;
    } else dayjs().subtract(2, 'month');
  };

  return (
    <div>
      <FormDialog
        title={isSync ? LEAVE_UPLOAD.SYNC_LEAVE_LABEL : LEAVE_UPLOAD.ADD_BUTTON_TEXT}
        open={openAddModal}
        button1Action={handleCloseWithReset}
        dialogPaperCss="leave-upload-dialog-paper"
        dialogCss="leave-upload-dialog"
        dialogBodycontent={
          <div className="leave-upload-dialog-body">
            <div className="leave-upload-start-date">
              <CommonDatePicker
                textCss="effort-upload-start-date-field"
                label={isSync ? LEAVE_UPLOAD.START_DATE_LABEL : LEAVE_UPLOAD.SELECT_START_DATE}
                minDate={getMinDate()}
                maxDate={dayjs().endOf('month')}
                placeholder={LEAVE_UPLOAD.PLACEHOLDER}
                autoFocus={autoFocus}
                value={startDateValue ? dayjs(startDateValue) : null}
                onChange={(date) =>
                  handleStartDateChange({
                    name: 'startDate',
                    value: date,
                  })
                }
                id={'add-start-date-text'}
                className="custom-calender"
              />
              {startDateError && (
                <div
                  className={'leave-upload-error'}
                >
                  <small>{startDateError}</small>
                </div>
              )}
            </div>
            <div className="leave-upload-start-date">
              <CommonDatePicker
                textCss="effort-upload-end-date-field"
                label={isSync ? LEAVE_UPLOAD.END_DATE_LABEL : LEAVE_UPLOAD.SELECT_END_DATE}
                minDate={isSync ? dayjs().subtract(1, 'month') : dayjs().subtract(2, 'month')}
                maxDate={dayjs().endOf('month')}
                placeholder={LEAVE_UPLOAD.PLACEHOLDER}
                autoFocus={autoFocus}
                value={endDateValue ? dayjs(endDateValue) : null}
                onChange={(date) =>
                  handleEndDateChange({
                    name: 'endDate',
                    value: date,
                  })
                }
                id={'add-end-date-text'}
                className="custom-calender"
              />
              {endDateError && (
                <div
                  className={'leave-upload-error'}
                >
                  <small>{endDateError}</small>
                </div>
              )}
            </div>

          </div>
        }
        button1={CANCEL_TEXT}
        button2={isSync ? LEAVE_UPLOAD.SYNC_LEAVE_UPLOAD : LEAVE_UPLOAD.RUN_BUTTON}
        button2Action={handleSubmit}
        cancelButtonId={'manual-leave-upload-cancel-btn'}
        submitButtonId={'manual-leave-upload-submit-btn'}
        isPopUp = {true}
      />
    </div>
  );
};

ManualLeaveUploadPopup.propTypes = {
  openAddModal: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  runBy: PropTypes.string.isRequired,
  setRows: PropTypes.func.isRequired,
  isSync: PropTypes.bool,
};

export default ManualLeaveUploadPopup;
